// Resolves an opaque storage key to a URL the browser can fetch.
//
// Production storage keys are short opaque identifiers (e.g.
// `members/<id>/<uuid>`). They are routed through the API so it can
// presign / proxy / authorise the underlying object-store fetch.
//
// Seed and demo data may store a fully-qualified URL directly in the
// same column (e.g. `https://images.unsplash.com/photo-...`). Those are
// already directly fetchable, so we pass them through unchanged.
//
// One module owns this rule so every projection that surfaces a member
// photo / group cover / event hero URL agrees on it. Lives in shared so
// apps/web (server components, mappers) and apps/server (projections)
// import the same source.
const ABSOLUTE_URL_PREFIXES = ['https://', 'http://'];
export function isAbsoluteMediaUrl(value) {
    return ABSOLUTE_URL_PREFIXES.some((p) => value.startsWith(p));
}
export function mediaUrlFromKey(key) {
    if (key === null)
        return null;
    if (isAbsoluteMediaUrl(key))
        return key;
    return `/api/v1/media/${encodeURIComponent(key)}`;
}
export function photoUrlFromKey(key, status) {
    if (key === null)
        return null;
    if (isAbsoluteMediaUrl(key))
        return key;
    if (status !== 'available')
        return null;
    return `/api/v1/media/${encodeURIComponent(key)}`;
}
// Size variants. Renditions are stored under deterministic sibling keys
// (`<key>/thumb`, `<key>/card`) written at upload-commit time — the same
// scheme as the PDF page-1 thumbnail (`<key>/thumb.jpg`). The variant is
// part of the storage key (path-encoded, never a query param) because the
// CDN cache policy for /api/v1/media/* excludes query strings from the
// cache key.
//
//   thumb — 256px wide: avatars, logos, search hits, link-preview images
//   card  — 960px wide: card/list covers, feed gallery tiles
//   (full — the base key: detail heroes, lightbox, pre-crop originals)
//
// generateImageVariants only writes a rendition when the source is wider
// than the target and is a resizable raster (never a GIF). When the object
// is absent the get-media route still serves the base object, but through a
// non-immutable fallback cache header plus an extra store round trip. So a
// caller that knows the exact set written (persisted on the owning row)
// passes it as `variants`: the URL points at the rendition only when it
// exists, otherwise at the base object directly (immutable), skipping the
// fallback. `variants` is required so every caller decides explicitly;
// `null` means "unknown" (a legacy row written before the set was persisted,
// or a surface that does not persist it yet) — keep the pre-persistence
// behavior of minting the variant URL and letting the fallback cover an
// absent rendition.
export const MEDIA_VARIANTS = ['thumb', 'card'];
export function mediaVariantKey(key, variant) {
    return `${key}/${variant}`;
}
// Whether a variant URL may be minted for `variant`: the persisted set
// includes it, or the set is unknown (null — legacy row). For projections whose
// contract is "the rendition URL, or null when there is none".
export function hasMediaVariant(variants, variant) {
    return variants === null || variants.includes(variant);
}
export function mediaVariantUrlFromKey(key, variant, variants) {
    if (key === null)
        return null;
    if (isAbsoluteMediaUrl(key))
        return key;
    return hasMediaVariant(variants, variant)
        ? mediaUrlFromKey(mediaVariantKey(key, variant))
        : mediaUrlFromKey(key);
}
export function photoVariantUrlFromKey(key, status, variant, variants) {
    if (key === null)
        return null;
    if (isAbsoluteMediaUrl(key))
        return key;
    if (status !== 'available')
        return null;
    return hasMediaVariant(variants, variant)
        ? mediaUrlFromKey(mediaVariantKey(key, variant))
        : mediaUrlFromKey(key);
}
//# sourceMappingURL=media-url.js.map